import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Pencil, Check, X, Power, Download, Upload, Search } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api, { formatDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StickyContextBar from "@/components/StickyContextBar";
import ConfirmDialog from "@/components/ConfirmDialog";

/**
 * FinanceMasterPage — 149-cw-r131 (2026-08-31).
 * Admin (BOD Lv1) kelola master data akuntansi:
 * - Chart of Accounts (Akun Piutang) — code, name, type
 * - Cost Centers (Pusat Biaya) — code, name, category
 * Operasi: tambah, edit code/nama/kategori, nonaktifkan (soft; entri default protected).
 * Entri nonaktif otomatis hilang dari dropdown detail invoice, tapi snapshot nama
 * di historical invoice tetap terpelihara.
 */
export const COA_TYPES = [
  { value: "receivable", label: "Receivable — Piutang" },
  { value: "payable",    label: "Payable — Utang" },
  { value: "other",      label: "Lainnya" },
];
const CC_CATEGORIES = [
  { value: "project", label: "Project — Proyek" },
  { value: "office",  label: "Office — Kantor" },
  { value: "shared",  label: "Shared — Umum" },
];

export default function FinanceMasterPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canManage = user?.division === "BOD" && Number(user?.level) === 1;
  const [tab, setTab] = useState("coa");
  const [coaList, setCoaList] = useState([]);
  const [ccList, setCcList] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [{ data: coa }, { data: cc }] = await Promise.all([
        api.get("/finance/chart-of-accounts?include_inactive=true"),
        api.get("/finance/cost-centers?include_inactive=true"),
      ]);
      setCoaList(Array.isArray(coa) ? coa : []);
      setCcList(Array.isArray(cc) ? cc : []);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal memuat master data");
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);

  if (!canManage) {
    return (
      <div className="p-6" data-testid="finance-master-no-access">
        <div className="max-w-md mx-auto mt-16 border border-rose-200 bg-rose-50 p-6 text-center">
          <div className="font-bold text-rose-800 mb-1">Akses Ditolak</div>
          <p className="text-xs text-rose-700">Halaman ini khusus BOD Lv1. Silakan hubungi administrator.</p>
          <Link to="/settings" className="text-xs text-[#0B1D3A] hover:underline inline-flex items-center gap-1 mt-3">
            <ArrowLeft size={12} /> Kembali ke Settings
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4" data-testid="finance-master-page">
      <StickyContextBar segments={["Settings", "Master Data Keuangan"]} />
      {/* 2026-06-20 (user request): tombol back di Master Data Keuangan. */}
      <button type="button" data-testid="fm-back-btn" onClick={() => nav("/settings")}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-[#0B1D3A] transition-colors">
        <ArrowLeft size={11} /> Kembali ke Settings
      </button>
      <div>
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Administration</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0B1D3A] tracking-tight mt-1 font-display">Master Data Keuangan</h1>
        <p className="text-xs text-slate-500 mt-1">
          Kelola daftar Akun Perkiraan &amp; Pusat Biaya untuk referensi akuntansi. Entri yang dinonaktifkan
          hilang dari dropdown pemilih tetapi snapshot nama pada invoice historis tetap terpelihara.
        </p>
      </div>

      <div className="border-b border-slate-200 flex gap-1" role="tablist">
        <TabBtn active={tab === "coa"} onClick={() => setTab("coa")} testid="fm-tab-coa">
          Akun Perkiraan <span className="ml-1 text-[10px] text-slate-400">({coaList.filter(x => x.is_active).length}/{coaList.length})</span>
        </TabBtn>
        <TabBtn active={tab === "cc"} onClick={() => setTab("cc")} testid="fm-tab-cc">
          Pusat Biaya <span className="ml-1 text-[10px] text-slate-400">({ccList.filter(x => x.is_active).length}/{ccList.length})</span>
        </TabBtn>
      </div>

      {tab === "coa" && (
        <MasterTable
          items={coaList}
          type="coa"
          typeOptions={COA_TYPES}
          typeField="type"
          endpoint="/finance/chart-of-accounts"
          labelName="Akun Perkiraan"
          codePlaceholder="e.g., 1106"
          namePlaceholder="e.g., Piutang Ekspor"
          onReload={loadAll}
          loading={loading}
          templateUrl="/finance/chart-of-accounts/template.xlsx"
          importUrl="/finance/chart-of-accounts/import"
          templateFilename="template_akun_perkiraan.xlsx"
          showKeywords
        />
      )}
      {tab === "cc" && (
        <MasterTable
          items={ccList}
          type="cc"
          typeOptions={CC_CATEGORIES}
          typeField="category"
          endpoint="/finance/cost-centers"
          labelName="Pusat Biaya"
          codePlaceholder="e.g., CC-OFC-04"
          namePlaceholder="e.g., Cabang Surabaya"
          onReload={loadAll}
          loading={loading}
        />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button type="button" onClick={onClick} role="tab" aria-selected={active} data-testid={testid}
      className={`px-4 py-2 text-xs font-semibold border ${active ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-600 border-transparent hover:bg-slate-100 hover:text-[#0B1D3A]"}`}>
      {children}
    </button>
  );
}

export function MasterTable({ items, type, typeOptions, typeField, endpoint, labelName, codePlaceholder, namePlaceholder, onReload, loading, templateUrl, importUrl, templateFilename, showKeywords = false }) {
  const [importing, setImporting] = useState(false);
  const downloadTemplate = async () => {
    try {
      const { data } = await api.get(templateUrl, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = templateFilename || "template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal mengunduh template"); }
  };
  const onImportFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setImporting(true);
    try {
      const { data } = await api.post(importUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import selesai: ${data.added} ditambahkan${data.updated_keywords ? `, ${data.updated_keywords} kata kunci diperbarui` : ""}${data.skipped ? `, ${data.skipped} dilewati` : ""}${data.errors?.length ? `, ${data.errors.length} error` : ""}`);
      onReload?.();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal mengimpor file");
    } finally { setImporting(false); }
  };
  const [newForm, setNewForm] = useState({ code: "", name: "", [typeField]: typeOptions[0]?.value || "" });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // {id, code, name, type/category}
  const [confirmDeact, setConfirmDeact] = useState({ open: false, item: null });
  // 2026-02-13 (user request): kolom pencarian — filter lokal kode/nama (+ kata kunci utk COA).
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return items;
    return items.filter((it) =>
      (it.code || "").toLowerCase().includes(query)
      || (it.name || "").toLowerCase().includes(query)
      || (Array.isArray(it.keywords) && it.keywords.some((k) => (k || "").toLowerCase().includes(query)))
    );
  }, [items, q]);

  const submit = async () => {
    if (!newForm.code.trim() || !newForm.name.trim()) { toast.error("Kode & Nama wajib diisi"); return; }
    setBusy(true);
    try {
      await api.post(endpoint, { code: newForm.code.trim(), name: newForm.name.trim(), [typeField]: newForm[typeField] });
      toast.success(`${labelName} baru tersimpan`);
      setNewForm({ code: "", name: "", [typeField]: typeOptions[0]?.value || "" });
      onReload();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || `Gagal menyimpan ${labelName}`);
    } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!editing || !editing.code.trim() || !editing.name.trim()) { toast.error("Kode & Nama wajib"); return; }
    setBusy(true);
    try {
      const body = { code: editing.code.trim(), name: editing.name.trim(), [typeField]: editing[typeField] };
      if (showKeywords) body.keywords = editing.keywords || [];
      await api.patch(`${endpoint}/${editing.id}`, body);
      toast.success("Perubahan tersimpan");
      setEditing(null);
      onReload();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal menyimpan perubahan");
    } finally { setBusy(false); }
  };

  const toggleActive = async (item) => {
    setBusy(true);
    try {
      await api.patch(`${endpoint}/${item.id}`, { is_active: !item.is_active });
      toast.success(item.is_active ? "Entri dinonaktifkan" : "Entri diaktifkan kembali");
      onReload();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal mengubah status");
    } finally { setBusy(false); setConfirmDeact({ open: false, item: null }); }
  };

  const typeLabel = (val) => typeOptions.find((o) => o.value === val)?.label || val;

  return (
    <div className="space-y-3" data-testid={`fm-panel-${type}`}>
      {/* 2026-06-20 (user request): tombol Download Template & Import Excel (COA only). */}
      {templateUrl && importUrl && (
        <div className="flex items-center gap-2 justify-end" data-testid={`fm-${type}-import-bar`}>
          <button type="button" data-testid={`fm-${type}-template-btn`} onClick={downloadTemplate}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-300 text-slate-700 hover:border-[#0B1D3A] hover:text-[#0B1D3A] text-[10px] uppercase tracking-wider font-semibold transition-colors">
            <Download size={11} /> Download Template
          </button>
          <label data-testid={`fm-${type}-import-btn`}
            className={`inline-flex items-center gap-1 px-2.5 py-1.5 bg-[#0B1D3A] text-white text-[10px] uppercase tracking-wider font-semibold cursor-pointer transition-colors ${importing ? "opacity-50 cursor-wait" : "hover:bg-[#12284d]"}`}>
            <Upload size={11} /> {importing ? "Mengimpor…" : "Import Excel"}
            <input type="file" accept=".xlsx" className="hidden" onChange={onImportFile} disabled={importing} data-testid={`fm-${type}-import-file`} />
          </label>
        </div>
      )}
      {/* Row: add new */}
      <div className="border border-slate-200 bg-slate-50 p-3 grid grid-cols-1 md:grid-cols-[140px_1fr_180px_auto] gap-2 items-end" data-testid={`fm-${type}-new`}>
        <div className="space-y-1">
          <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Kode *</label>
          <Input value={newForm.code} onChange={(e) => setNewForm({ ...newForm, code: e.target.value })}
            placeholder={codePlaceholder} data-testid={`fm-${type}-code`} className="h-9 rounded-none font-mono" />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Nama *</label>
          <Input value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
            placeholder={namePlaceholder} data-testid={`fm-${type}-name`} className="h-9 rounded-none" />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">{typeField === "type" ? "Tipe" : "Kategori"}</label>
          <Select value={newForm[typeField]} onValueChange={(v) => setNewForm({ ...newForm, [typeField]: v })}>
            <SelectTrigger className="rounded-none h-9 bg-white" data-testid={`fm-${type}-type`}><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              {typeOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <button type="button" onClick={submit} disabled={busy}
          data-testid={`fm-${type}-submit`}
          className="h-9 px-4 bg-[#0B1D3A] hover:bg-[#122a4d] text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
          <Plus size={12} /> Tambah
        </button>
      </div>

      {/* 2026-02-13 (user request): kolom pencarian di atas tabel. */}
      <div className="flex items-center gap-2 justify-end" data-testid={`fm-${type}-search-bar`}>
        <div className="relative w-full md:w-80">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={`Cari kode / nama${type === "coa" ? " / kata kunci" : ""}…`}
            data-testid={`fm-${type}-search`}
            className="h-9 rounded-none pl-8 bg-white" />
        </div>
        {q.trim() && (
          <span className="text-[10px] text-slate-500 tabular-nums whitespace-nowrap" data-testid={`fm-${type}-search-count`}>
            {filtered.length} dari {items.length}
          </span>
        )}
      </div>

      {/* Table */}
      <div className="border border-slate-200 overflow-x-auto bg-white">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-3 py-2.5 font-semibold text-slate-500 text-[10px] uppercase tracking-wider w-[100px]">Kode</th>
              <th className="px-3 py-2.5 font-semibold text-slate-500 text-[10px] uppercase tracking-wider">Nama</th>
              <th className="px-3 py-2.5 font-semibold text-slate-500 text-[10px] uppercase tracking-wider w-[180px]">{typeField === "type" ? "Tipe" : "Kategori"}</th>
              {showKeywords && (
                <th className="px-3 py-2.5 font-semibold text-slate-500 text-[10px] uppercase tracking-wider min-w-[260px]" data-testid="fm-coa-keywords-header">Kata Kunci Auto-Klasifikasi</th>
              )}
              <th className="px-3 py-2.5 font-semibold text-slate-500 text-[10px] uppercase tracking-wider w-[90px]">Status</th>
              <th className="px-3 py-2.5 font-semibold text-slate-500 text-[10px] uppercase tracking-wider w-[140px] text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 && (
              <tr><td colSpan={showKeywords ? 6 : 5} className="text-center py-8 text-slate-400 italic">Memuat…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={showKeywords ? 6 : 5} className="text-center py-8 text-slate-400 italic">
                {q.trim() ? `Tidak ada hasil yang cocok untuk "${q.trim()}".` : `Belum ada ${labelName.toLowerCase()}.`}
              </td></tr>
            )}
            {filtered.map((it) => {
              const isEditing = editing?.id === it.id;
              return (
                <tr key={it.id} className={`border-b border-slate-100 ${it.is_active ? "" : "bg-slate-50 opacity-70"}`} data-testid={`fm-${type}-row-${it.id}`}>
                  <td className="px-3 py-2 font-mono text-[11px] text-[#0B1D3A] font-semibold">
                    {isEditing ? (
                      <Input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value })} className="h-7 rounded-none font-mono text-[11px]" />
                    ) : it.code}
                  </td>
                  <td className="px-3 py-2 text-slate-800">
                    {isEditing ? (
                      <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="h-7 rounded-none" />
                    ) : it.name}
                    {it.is_default && <span className="ml-2 text-[9px] uppercase tracking-wider text-emerald-700 border border-emerald-300 bg-emerald-50 px-1 py-0.5">Default</span>}
                    {it.is_parent && <span className="ml-2 text-[9px] uppercase tracking-wider text-indigo-700 border border-indigo-300 bg-indigo-50 px-1 py-0.5" title="Akun induk — tidak muncul pada pilihan COA transaksi; yang dipilih adalah sub-akunnya">Induk</span>}
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {isEditing ? (
                      <Select value={editing[typeField]} onValueChange={(v) => setEditing({ ...editing, [typeField]: v })}>
                        <SelectTrigger className="rounded-none h-7 text-[11px] bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent className="rounded-none">
                          {typeOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : typeLabel(it[typeField])}
                  </td>
                  {showKeywords && (
                    <td className="px-3 py-2 text-slate-600" data-testid={`fm-${type}-keywords-cell-${it.id}`}>
                      {isEditing ? (
                        <KeywordChipsEditor
                          value={editing.keywords || []}
                          onChange={(kws) => setEditing({ ...editing, keywords: kws })}
                          testid={`fm-${type}-keywords-${it.id}`}
                        />
                      ) : (
                        <KeywordChipsReadonly keywords={it.keywords} />
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2">
                    {it.is_active
                      ? <span className="inline-block px-2 py-0.5 text-[10px] uppercase font-semibold tracking-wider border border-emerald-300 bg-emerald-50 text-emerald-700">Aktif</span>
                      : <span className="inline-block px-2 py-0.5 text-[10px] uppercase font-semibold tracking-wider border border-slate-300 bg-slate-50 text-slate-500">Nonaktif</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {isEditing ? (
                      <div className="inline-flex gap-1">
                        <button onClick={saveEdit} disabled={busy} data-testid={`fm-${type}-save-${it.id}`}
                          className="p-1.5 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50" title="Simpan">
                          <Check size={13} />
                        </button>
                        <button onClick={() => setEditing(null)} data-testid={`fm-${type}-cancel-${it.id}`}
                          className="p-1.5 text-slate-500 hover:bg-slate-100" title="Batal">
                          <X size={13} />
                        </button>
                      </div>
                    ) : (
                      <div className="inline-flex gap-1">
                        <button onClick={() => setEditing({ id: it.id, code: it.code, name: it.name, [typeField]: it[typeField], keywords: Array.isArray(it.keywords) ? [...it.keywords] : [] })}
                          data-testid={`fm-${type}-edit-${it.id}`}
                          className="p-1.5 text-slate-600 hover:bg-slate-100" title="Edit">
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => setConfirmDeact({ open: true, item: it })}
                          disabled={it.is_default && it.is_active}
                          data-testid={`fm-${type}-toggle-${it.id}`}
                          className={`p-1.5 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed ${it.is_active ? "text-rose-600" : "text-emerald-600"}`}
                          title={it.is_active ? "Nonaktifkan" : "Aktifkan"}>
                          <Power size={13} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showKeywords && (
        <div className="text-[10px] text-slate-500 italic border-l-2 border-slate-300 pl-2" data-testid="fm-keywords-hint">
          <b>Tips</b>: Tambahkan kata kunci per akun (mis. "pembayaran invoice", "biaya admin"). Saat baris E-Statement diklasifikasi di tab Breakdown/Klasifikasi Mutasi, sistem akan menyarankan akun ini bila deskripsi mutasi mengandung salah satu kata kunci (case-insensitive).
        </div>
      )}

      <ConfirmDialog
        open={confirmDeact.open}
        onOpenChange={(o) => setConfirmDeact({ ...confirmDeact, open: o })}
        title={confirmDeact.item?.is_active ? `Nonaktifkan ${labelName}?` : `Aktifkan ${labelName}?`}
        description={
          confirmDeact.item?.is_active
            ? `Entri "${confirmDeact.item?.code} — ${confirmDeact.item?.name}" akan hilang dari dropdown pemilih. Referensi historis tetap terpelihara.`
            : `Entri "${confirmDeact.item?.code} — ${confirmDeact.item?.name}" akan muncul kembali di dropdown pemilih.`
        }
        confirmLabel={confirmDeact.item?.is_active ? "Nonaktifkan" : "Aktifkan"}
        confirmVariant={confirmDeact.item?.is_active ? "destructive" : "default"}
        onConfirm={() => toggleActive(confirmDeact.item)}
      />
    </div>
  );
}

/**
 * 2026-02 (user request): Chip editor untuk kata kunci COA. Ketik lalu Enter atau
 * koma untuk menambah chip; klik ✕ pada chip untuk menghapus. Semua chip disimpan
 * lowercase & di-dedupe di backend.
 */
function KeywordChipsEditor({ value, onChange, testid }) {
  const [draft, setDraft] = useState("");
  const arr = Array.isArray(value) ? value : [];
  const commit = (raw) => {
    const parts = raw.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (parts.length === 0) return;
    const merged = [...arr];
    for (const p of parts) {
      if (p.length < 2 || p.length > 80) continue;
      if (!merged.includes(p)) merged.push(p);
      if (merged.length >= 40) break;
    }
    onChange(merged);
    setDraft("");
  };
  const removeAt = (i) => onChange(arr.filter((_, idx) => idx !== i));
  return (
    <div className="flex flex-wrap items-center gap-1 border border-slate-300 bg-white px-1.5 py-1 min-h-[28px]" data-testid={testid}>
      {arr.map((k, i) => (
        <span key={`${k}-${i}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#0B1D3A] text-white text-[10px]" data-testid={`${testid}-chip-${i}`}>
          {k}
          <button type="button" onClick={() => removeAt(i)} className="hover:text-rose-300" title="Hapus" data-testid={`${testid}-remove-${i}`}>
            <X size={10} />
          </button>
        </span>
      ))}
      <input value={draft} onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commit(draft); }
          else if (e.key === "Backspace" && !draft && arr.length > 0) { removeAt(arr.length - 1); }
        }}
        onBlur={() => draft.trim() && commit(draft)}
        placeholder={arr.length === 0 ? "Ketik kata kunci, Enter atau koma…" : "+ tambah…"}
        data-testid={`${testid}-input`} maxLength={80}
        className="flex-1 min-w-[100px] h-6 text-[11px] bg-transparent border-0 focus:outline-none placeholder:text-slate-400" />
    </div>
  );
}

function KeywordChipsReadonly({ keywords }) {
  const arr = Array.isArray(keywords) ? keywords : [];
  if (arr.length === 0) return <span className="text-[10px] text-slate-400 italic">— belum ada —</span>;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {arr.map((k, i) => (
        <span key={`${k}-${i}`} className="inline-block px-1.5 py-0.5 border border-slate-300 bg-slate-50 text-slate-700 text-[10px]">{k}</span>
      ))}
    </div>
  );
}
